/**
 * Returns capitalized text, ignores apostrophe-s, applies title case if ampersand present.
 * @param value BusinessName, FirstName, LastName or FullName
 * @param isBusinessName optional boolean, defaults to false, true when value equals BusinessName
 */
export const textCapitalize = (value: string = '', isBusinessName: boolean = false): string => {
    let formatted;
    if (isBusinessName !== true) {
        formatted = (value ?? '')
            .trim()
            .toLowerCase()
            // remove multiple spaces
            .replace(/\s+/g, ' ')
            // standard spacing after commas
            .replace(/\s*,\s*/g, ', ')
            // e.g. anna-maria -> Anna-Maria
            .replace(/(\p{L})(-)(\p{L})/gu, (_, a, b, c) => `${a.toLowerCase()}${b}${c.toUpperCase()}`)
            // e.g. mcgraw -> McGraw, macdonald -> MacDonald
            .replace(/\bm(a?c)(\p{L})/gu, (_, a, b) => `M${a}${b.toUpperCase()}`)
            // e.g. o'connor -> O'Connor, xi'an -> Xi'An (> 1 letter following apostrophe or dash)
            .replace(/\b(\p{L})(\p{L}*['-])(\p{L})(?=\p{L})/gu, (_, a, b, c) => `${a.toUpperCase()}${b}${c.toUpperCase()}`)
            // e.g. mark's -> Mark's, james' -> James' (0 or 1 letter following apostrophe)
            .replace(/\b(\p{L})(\p{L}+'\p{L}?)(?=\s|\b|$)/gu, (_, a, b) => `${a.toUpperCase()}${b}`)
            // capitalize first letter of terms with only letters
            .replace(/(?<=\s|^)(\p{L})(?=\p{L}*(?:\s|\.|,|$))/gu, (m) => m.toUpperCase());
    }
    else {
        formatted = (value ?? '')
            .trim()
            .toLowerCase()
            // remove multiple spaces
            .replace(/\s+/g, ' ')
            // standard spacing after commas
            .replace(/\s*,\s*/g, ', ')
            // e.g. a-action -> A-Action, a/c -> A/C, grub-n-go -> Grub-N-Go
            .replace(/((?<=\b)\p{L}{1}(?=[^\s/-]*(?:[-]|[/])))|((?:(?<=[-]|[/]))\p{L}{1})/gu, (m) => m.toUpperCase())
            // e.g. mcgraw -> McGraw, macdonald -> MacDonald
            .replace(/\bm(a?c)(\p{L})/gu, (_, a, b) => `M${a}${b.toUpperCase()}`)
            // e.g. o'connor -> O'Connor, xi'an -> Xi'An (> 1 letter following apostrophe or dash)
            .replace(/\b(\p{L})(\p{L}*['-])(\p{L})(?=\p{L})/gu, (_, a, b, c) => `${a.toUpperCase()}${b}${c.toUpperCase()}`)
            // e.g. mark's -> Mark's, james' -> James' (0 or 1 letter following apostrophe)
            .replace(/\b(\p{L})(\p{L}+'\p{L}?)(?=\s|\b|$)/gu, (_, a, b) => `${a.toUpperCase()}${b}`)
            /*
             * Adds spaces around ampersand when there are more than 2 characters on either side of the ampersand and
             * ampersand has no space on at least one side
             * For example:
             * Cook's A/C&Heating, Inc. -> Cook's A/C & Heating, Inc.
             * Cook's A/C &Heating, Inc. -> Cook's A/C & Heating, Inc.
             * Cook's A/C& Heating, Inc. -> Cook's A/C & Heating, Inc.
             * I want to note it will not add spaces in the case of this example: Cook's A/C & Heating, Inc. -> Cook's A/C & Heating, Inc.
             */
            .replace(/(?<=\b[\p{L}\p{N}\p{S}\p{M}\p{P}]{3,})((?<!\s+)&\s+|(?<!\s+)&(?!\s+)|\s+&(?!\s+))(?=[\p{L}\p{N}\p{S}\p{M}\p{P}]{3,}\b)/gu, ' & ')
            // capitalize first letter of terms with only letters
            .replace(/(?<=\s|^)(\p{L})(?=\p{L}*(?:\s|\.|,|$))/gu, (m) => m.toUpperCase())
            /*
             * capitalize letters that precede an ampersand for businesses such as: AT&T, 3M&C, etc.
             * e.g. At&T -> AT&T, 3m&C -> 3M&C
             */
            .replace(/(?<![\p{L}\p{N}\p{S}\p{P}\p{M}]{1,})([\p{L}\p{N}]{1,2})(?=\s*&\s*(?=[\p{L}\p{N}]{1,2}(?![\p{S}\p{P}\p{M}])\b))/gu, (m) => m.toUpperCase())
            /*
             * capitalize letters that trail an ampersand for businesses such as: AT&T, 3M&C, etc.
             * e.g. AT&Tt -> AT&TT, A&mM -> A&MM
             */
            .replace(/(?<=(?<=\b(?<![\p{S}\p{P}\p{M}])[\p{L}\p{N}]{1,2})\s*&\s*)([\p{L}]{1,2})(?![\p{S}\p{P}\p{M}\p{L}\p{N}]{1,})/gu, (m) => m.toUpperCase())
            /*
             * Removes spaces around ampersand when there are at most 2 letters on either side of the ampersand
             * e.g. AT & T -> AT&T, M &M -> M&M, A& M -> A&M, 3M & C -> 3M&C
             */
            .replace(/(?<=\b(?<![\p{S}\p{P}\p{M}])[\p{L}\p{N}]{1,2})(\s*&\s+|\s+&\s*)(?=[\p{L}\p{N}]{1,2}(?![\p{S}\p{P}\p{M}])\b)/gu, '&');
    }

    // 1 and 2-letter terms in all uppercase if ampersand present (i.e. business name with ampersand)
    return formatted.includes('&') ? formatted.replace(/(?<=\s|^)(\p{L}{1,2})(?=\s|,|$)/gu, (m) => m.toUpperCase()) : formatted;
};
